import { Map, OrderedMap, List } from 'immutable';
import keyBinder from 'shared/lib/keyBinder';
import {
  isCompact,
  isGrid,
  isGlobalElementManager,
} from 'main/components/show/cuesheet/cuesheetViewHelper';
import { DisplayTypes } from 'shared/constants/FlowConstants';
import { EditableCellTypes, KeyScopes, RowTypes } from './CuesheetConstants';
import { getDefaultRTEState } from 'shared/lib/rteHelper';
import mobileCheck from 'shared/lib/mobileCheck';

import {
  findRowPosition,
  getFirstSelectedCell,
  getLastSelectedCell,
  getLastSelectedCellRowIndex,
  getSelectedCell,
} from 'flux/stores/cuesheet/CuesheetCursorHelper';

const isMobile = mobileCheck();
const ItemTitleCellSpec = Map({
  type: EditableCellTypes.ItemTitle,
  value: EditableCellTypes.ItemTitle,
});

const _isMultiSelectable = (type) =>
  [
    EditableCellTypes.Data,
    EditableCellTypes.ItemDescription,
    EditableCellTypes.ItemTitle,
    EditableCellTypes.ItemNote,
    EditableCellTypes.ItemDuration,
    EditableCellTypes.ItemPresetDuration,
    EditableCellTypes.ItemStartTime,
  ].indexOf(type) >= 0;

const _isRTEEditable = (type) =>
  [
    EditableCellTypes.Data,
    EditableCellTypes.ItemDescription,
    EditableCellTypes.ItemTitle,
    EditableCellTypes.ItemNote,
  ].indexOf(type) >= 0;

/**
 * CuesheetCursor Mixin
 *
 */
export default {
  onClearCursor() {
    this.setStateAndTrigger(this.getClearedCursorState());
  },

  onClearEditing() {
    //_getClearedEditState is from CuesheetCellMixin. TODO change the method name to indicate that
    //it is not private (no _ in function name)
    this.setStateAndTrigger(this._getClearedEditState());
  },

  _moveColumnHeaderCursor(direction) {
    let nextState = this.getState();
    const selectedColumnId = this.getSelectedColumnId();

    nextState = nextState.withMutations((mutableState) => {
      const colOrder = mutableState.get('columnOrder');

      const selectedColIndex = colOrder.indexOf(selectedColumnId);
      let nextColIndex = selectedColIndex;

      if (direction === 'right') ++nextColIndex;

      if (direction === 'left') --nextColIndex;

      const nextCol = mutableState.getIn([
        'columns',
        colOrder.get(nextColIndex),
      ]);
      const nextColId = nextCol ? nextCol.get('_id') : colOrder.first();

      const cellSpec = { type: EditableCellTypes.Data, value: nextColId };

      mutableState
        .set('lastAction', 'setCursor')
        .set('lastActionTimestamp', new Date().valueOf())
        .deleteIn(['columns', selectedColumnId, 'selected'])
        .setIn(['columns', nextColId, 'selected'], true)
        .set('scrollColumnIndex', this._getScrollColumnIndex(Map(cellSpec)));

      return mutableState;
    });

    this.setStateAndTrigger(nextState);
  },

  onMoveCursorLeft(disableBulkAction) {
    this._setSelectedCellMovementDirection('left');

    if (this._isHeaderCellSelected()) {
      return this._moveColumnHeaderCursor('left');
    }

    this._clearColumnHeaderSelectedCellSelection();

    const selection = this._moveCursorSelectionHelper();
    const selectedCellType = selection.cellSpec.get('type');
    let selectedCellIndex = this._getCellIndex(selection.cellSpec);
    let selectableCells = this.getInState(['selectableCells']);

    // if bulk selecting and an editable cell is selected, skip over time cells
    if (
      keyBinder.shift &&
      _isRTEEditable(selectedCellType) &&
      !disableBulkAction
    ) {
      selectedCellIndex = this._getCellIndexWithoutTimes(selection.cellSpec);
      selectableCells = this._selectableCellsWithoutTimes();
    }

    if (this._isFirstCell(selection.rowIndex, selectedCellIndex)) return;

    let nextRowIndex = selection.rowIndex;
    let nextCellIndex = selectedCellIndex - 1;

    if (this._isFirstCellInRow(selectedCellIndex)) {
      nextCellIndex = selectedCellIndex;
    }

    const nextCellSpec = selectableCells.get(nextCellIndex);

    this.setCursor(nextRowIndex, nextCellSpec, false, disableBulkAction);
  },

  onMoveCursorRight(disableBulkAction) {
    this._setSelectedCellMovementDirection('right');

    if (this._isHeaderCellSelected()) {
      return this._moveColumnHeaderCursor('right');
    }

    this._clearColumnHeaderSelectedCellSelection();

    const state = this.getState();

    const selection = this._moveCursorSelectionHelper();
    const selectedCellType = selection.cellSpec.get('type');

    let selectedCellIndex = this._getCellIndex(selection.cellSpec);
    let selectableCells = state.get('selectableCells');

    // if bulk selecting and an editable cell is selected, skip over time cells
    if (keyBinder.shift && _isRTEEditable(selectedCellType)) {
      selectedCellIndex = this._getCellIndexWithoutTimes(selection.cellSpec);
      selectableCells = this._selectableCellsWithoutTimes();
    }

    if (this._isLastCell(selection.rowIndex, selectedCellIndex)) return;

    let nextRowIndex = selection.rowIndex;
    let nextCellIndex = selectedCellIndex + 1;

    if (this._isLastCellInRow(selectedCellIndex)) {
      nextCellIndex = selectedCellIndex;
    }

    const isTitleRow =
      state.getIn(['rows', nextRowIndex, 'type']) === 'TitleItem';

    if (
      selectableCells.get(nextCellIndex).get('type') === 'ItemDescription' &&
      isTitleRow
    ) {
      nextCellIndex++;
    }

    const nextCellSpec = selectableCells.get(nextCellIndex);

    this.setCursor(nextRowIndex, nextCellSpec, false, disableBulkAction);
  },

  _moveCursorVerticalToTitleRow(increment) {
    const moveUp = increment === -1;

    const selectedCellMovementDirection = moveUp ? 'up' : 'down';

    this._setSelectedCellMovementDirection(selectedCellMovementDirection);

    /**
     * Get current selection
     */
    const selection = this._moveCursorSelectionHelper();
    const { rowIndex } = selection;
    let nextRowIndex = selection.rowIndex + increment;
    let nextTitleRowIndex;

    if (moveUp) {
      if (this._isHeaderCellSelected() || rowIndex === 0) return;
      while (nextTitleRowIndex === undefined && nextRowIndex >= 0) {
        const rowSpec = this._rowGetter(nextRowIndex);
        const rowType = rowSpec.get('type');
        if (rowType === RowTypes.TitleItem) {
          nextTitleRowIndex = nextRowIndex;
          break;
        }
        nextRowIndex += increment;
      }
    }

    if (!moveUp) {
      if (this._isLastRow(rowIndex)) return;
      if (this._isHeaderCellSelected()) {
        this._clearColumnHeaderSelectedCellSelection();
      }
      while (
        !nextTitleRowIndex &&
        nextRowIndex < this.getInState('rows').size
      ) {
        const rowSpec = this._rowGetter(nextRowIndex);
        const rowType = rowSpec.get('type');
        if (rowType === RowTypes.TitleItem) {
          nextTitleRowIndex = nextRowIndex;
          break;
        }
        nextRowIndex += increment;
      }
    }

    if (nextTitleRowIndex || nextTitleRowIndex === 0) {
      this.setCursor(nextTitleRowIndex, ItemTitleCellSpec, false, true);
      this.setStateAndTrigger(
        this.getState().set('scrollRowWithOffsetIndex', nextTitleRowIndex),
      );
    }
  },

  _rowGetter(indx) {
    const rows = this.getInState('rows');

    // return empty object for footer row or if there are no rows in state
    // this can happen whenever the cuesheet state is cleared. For example,
    // when refreshing rows after an internet disconnect
    if (rows.size <= indx || !rows || !rows.get) return {};

    return rows.get(indx);
  },

  _moveCursorVertical(increment, disableBulkAction) {
    const moveUp = increment === -1;

    const selectedCellMovementDirection = moveUp ? 'up' : 'down';

    this._setSelectedCellMovementDirection(selectedCellMovementDirection);

    /**
     * Get current selection
     */
    const selection = this._moveCursorSelectionHelper();
    const selectedColumnId = this.getSelectedColumnId();

    /**
     * If first item title or desc cell or first cell of private notes column
     * is selected do nothing as they do not have selectable column headers
     */
    if (
      this._isFirstRow(selection.rowIndex) &&
      moveUp &&
      !this._isHeaderCellSelected()
    ) {
      const selectionType = selection.cellSpec.get('type');
      if (
        selectionType === EditableCellTypes.ItemTitle ||
        selectionType === EditableCellTypes.ItemNote ||
        selectionType === EditableCellTypes.ItemDescription ||
        selectionType === EditableCellTypes.ItemStartTime ||
        selectionType === EditableCellTypes.ItemPresetDuration ||
        selectionType === EditableCellTypes.ItemDuration
      ) {
        return;
      }
    }

    if (this._isFirstRow(selection.rowIndex) && moveUp) {
      this._selectHeaderCell(selectedColumnId);
      return;
    }

    /**
     * If a column header cell is selected & user navigates downward
     * select first data cell in the same column
     */
    if (!moveUp && this._isHeaderCellSelected()) {
      this._clearColumnHeaderSelectedCellSelection();

      selection.cellSpec.type = EditableCellTypes.Data;
      selection.cellSpec.value = selectedColumnId;

      this.setCursor(0, Map(selection.cellSpec), false, disableBulkAction);

      return;
    }

    if (!moveUp && this._isLastRow(selection.rowIndex)) return;
    if (moveUp && this._isHeaderCellSelected()) return;

    const nextRowIndex = selection.rowIndex + increment;

    this.setCursor(nextRowIndex, selection.cellSpec, false, disableBulkAction);
  },

  moveCursorFirstRow() {
    this.setCursor(0, ItemTitleCellSpec, false, true);
  },

  moveCursorLastRow() {
    const next = this.getState();
    let lastRowIndex = next.get('rows').size - 1;
    this.setCursor(lastRowIndex, ItemTitleCellSpec, false, true);
  },

  onMoveCursorToNextTitleRowAbove() {
    this._moveCursorVerticalToTitleRow(-1);
  },

  onMoveCursorToNextTitleRowBelow() {
    this._moveCursorVerticalToTitleRow(1);
  },

  onMoveCursorUp(disableBulkAction) {
    this._moveCursorVertical(-1, disableBulkAction);
  },

  onMoveCursorDown(disableBulkAction) {
    this._moveCursorVertical(1, disableBulkAction);
  },

  _setSelectedCellMovementDirection(selectedCellMovementDirection) {
    const displayType = this.getInState('show').get('displayType');

    if (displayType && displayType === DisplayTypes.show) return;

    this.setStateAndTrigger(
      this.getState().set(
        'selectedCellMovementDirection',
        selectedCellMovementDirection,
      ),
    );
  },

  /**
   * Get selection position for cursor movement
   *
   * This allows the cursor to be anchored to the first cell when you're
   * currently in multi-cell select mode but begin navigating without holding
   * the shift key down.
   *
   * This also allows you to correctly maintain the last
   * cell selection position while in multi-cell select mode.
   */
  _moveCursorSelectionHelper() {
    const state = this.getState(),
      itemSetId = state.get('rowMap').keySeq().first();

    /**
     * If no selected cells return default selected cell
     */
    const { rowId, cellSpec } =
      state.get('selectedCells').size > 1 && !keyBinder.shift
        ? getFirstSelectedCell(state)
        : this.getSelectedCellWithDefault();

    return { cellSpec, rowIndex: findRowPosition(itemSetId, rowId, state) };
  },

  /**
   * Check if a row index is the first in the entire data set
   */
  _isFirstRow(rowIndex) {
    return rowIndex === 0;
  },

  /**
   * Check if a row index is the last in the entire data set
   */
  _isLastRow(rowIndex) {
    return rowIndex === this.getInState('rows').size - 1;
  },

  /**
   * Check if a cell index is the first in the entire data set
   */
  _isFirstCell(rowIndex, cellIndex) {
    return this._isFirstRow(rowIndex) && this._isFirstCellInRow(cellIndex);
  },

  /**
   * Check if a cell index is the last in the entire data set
   */
  _isLastCell(rowIndex, cellIndex) {
    return this._isLastRow(rowIndex) && this._isLastCellInRow(cellIndex);
  },

  /**
   * Check if a cell index is the first in selectableCells
   */
  _isFirstCellInRow(cellIndex) {
    return cellIndex === 0;
  },

  /**
   * Check if a cell index is the last in selectableCells
   */
  _isLastCellInRow(cellIndex) {
    return cellIndex === this.getInState('selectableCells').size - 1;
  },

  /**
   * Get the index of a cellSpec from the selectable cells list
   */
  _getCellIndex(cellSpec) {
    const indx = this.getInState('selectableCells').findIndex((spec) =>
      spec.equals(cellSpec),
    );

    return indx;
  },

  /**
   * Get the index of a cellSpec from the selectable cells list
   */
  _getCellIndexWithoutTimes(cellSpec) {
    const indx = this._selectableCellsWithoutTimes().findIndex((spec) =>
      spec.equals(cellSpec),
    );

    return indx;
  },

  _getFixedCells() {
    let fixed = [];
    const timeCells = [];
    const displayType = this.getInState(['show', 'displayType']);

    if (this.getInState(['show', 'displayItemTitles'])) {
      fixed.push(Map({ type: EditableCellTypes.ItemTitle }));
    }

    if (this.getInState(['show', 'displayItemDescriptions']) && !isCompact()) {
      fixed.push(Map({ type: EditableCellTypes.ItemDescription }));
    }

    //Array of time cells
    if (this.getInState(['show', 'displayStartTime'])) {
      timeCells.push(Map({ type: EditableCellTypes.ItemStartTime }));
    }

    if (this.getInState(['show', 'displayPresetDuration'])) {
      timeCells.push(Map({ type: EditableCellTypes.ItemPresetDuration }));
    }

    if (this.getInState(['show', 'displayDuration'])) {
      timeCells.push(Map({ type: EditableCellTypes.ItemDuration }));
    }

    const isGlobalElementMgr = isGlobalElementManager();

    //Add time cells to fixed cells array. If show or GEM, push to end of fixed cells.
    //If grid, unshift to beginning of fixed cells.
    if (
      !displayType ||
      displayType === DisplayTypes.show ||
      isGlobalElementMgr
    ) {
      fixed = fixed.concat(timeCells);
    } else {
      fixed = timeCells.concat(fixed);
    }

    if (
      !this.getInState(['userSettings', 'noteColumn', 'hidden']) &&
      !isGlobalElementMgr
    ) {
      /**
       * On schedules and lists, the Private Notes column is first.
       */
      if (!displayType || displayType === 'show') {
        fixed.push(Map({ type: EditableCellTypes.ItemNote }));
      } else {
        fixed.unshift(Map({ type: EditableCellTypes.ItemNote }));
      }
    }

    return List(fixed);
  },

  getSelectableCellList(columnOrder) {
    const fixedCells = this._getFixedCells();

    return List(
      fixedCells.concat(
        columnOrder.map((colId) => {
          return Map({ type: EditableCellTypes.Data, value: colId });
        }),
      ),
    );
  },

  /**
   * Rebuild the selectable cells in state and return the new state
   */
  rebuildSelectableCellList() {
    return this.getState().set(
      'selectableCells',
      this.getSelectableCellList(this.getInState('columnOrder')),
    );
  },

  /**
   * Get first row+cell selected
   *
   * @returns Object - Example: {rowIndex, rowId, cellSpec}
   */
  getFirstSelectedCell(state) {
    state = state || this.getState();

    return getFirstSelectedCell(state);
  },

  /**
   * Get first cell selected row index
   *
   * If no cells are selected it will return -1
   *
   * @returns Number
   */
  getFirstSelectedCellRowIndex(state) {
    state = state || this.getState();

    const cell = this.getFirstSelectedCell(state);

    if (cell === -1) return -1;

    return cell.rowIndex;
  },

  /**
   * Get last row+cell selectied
   *
   * @returns Object - Example: {rowIndex, rowId, cellSpec}
   */
  getLastSelectedCell(state) {
    state = state || this.getState();

    return getLastSelectedCell(state);
  },

  /**
   * Get last cell selected row index
   *
   * If no cells are selected it will return -1
   *
   * @returns Number
   */
  getLastSelectedCellRowIndex(state) {
    state = state || this.getState();

    return getLastSelectedCellRowIndex(state);
  },

  /**
   * Get information about the currently selected cell. If this is used when
   * multiple cells are selected then it will return the last selected cell.
   *
   * What does with default mean?
   * If no cells have been selected by a user it will return the default cell -
   * the first upper left cell spec.
   *
   * returns an object that has the rowIndex and a cellSpec. Example:
   *
   * {rowIndex: 2, cell: {type: EditableCellTypes.Data, value: <columnId>} }
   *
   */
  getSelectedCellWithDefault() {
    return getSelectedCell(this.getState(), true);
  },

  getSelectedCell() {
    return getSelectedCell(this.getState());
  },

  /**
   * Get default cell.
   *
   * This when there is no cell selected and a user tries to navigate the
   * cursor. It will select the top leftmost cell.
   */
  _getDefaultSelectedCell() {
    const rowIndex = 0,
      rowId = this.findRowId(0);

    return {
      rowIndex,
      rowId,
      cellSpec: { type: EditableCellTypes.ItemTitle },
      default: true,
    };
  },

  /**
   * Get Information about the currently selected column
   */
  getSelectedColumnId() {
    const selectedColumn = this.getSelectedColumn();
    return selectedColumn ? selectedColumn.get('_id') : null;
  },

  getSelectedColumn() {
    const columns = this.getInState('columns');

    //Gets id of column through selected cuesheet cell
    const selectedCell = this.getSelectedCell();
    const cellSpec = selectedCell ? selectedCell.cellSpec : null;

    //Gets id of column through selected column header
    const selectedColumn = columns.find((column) => column.get('selected'));

    let column;

    if (selectedColumn) {
      column = selectedColumn;
    } else if (cellSpec) {
      column = columns.get(cellSpec.get('value'));
    }

    return column;
  },

  /**
   *
   * @param rowIndex index in the 'rows' List
   * @param cellSpec
   * @param skipScroll if true, scroll state is untouched
   *
   * example cellSpec (item title):
   *
   * {type: EditableCellTypes.ItemTitle}
   *
   * example cellSpec (item column data cell):
   *
   * {type: EditableCellTypes.Data, value: <columnId>}
   *
   */
  setCursor(rowIndex, cellSpec, skipScroll, disableBulkAction) {
    let nextState = this.getState();

    cellSpec = Map.isMap(cellSpec) ? cellSpec : Map(cellSpec);

    //Never set cursor for mobile devices, in support of:
    //https://github.com/SHOFLO/Issues/issues/3540
    //But we still need to select the row for shotgun view,
    //in support of:https://github.com/SHOFLO/Issues/issues/3675
    if (isMobile) {
      /**
       * Use clearRowFieldState (see CuesheetCoreMixin).
       * Then apply 'selected' value to the row that user has selected
       *
       * Fix for Issue#2078 (mobile shotgun issue)
       */
      nextState = this.clearRowFieldState('selected');

      /**
       * This is the legacy way of accounting for cue mobile shotgun
       * view. This will be RETIRED soon so no refactor will be done
       * as this will be removed in the new future.
       */
      nextState = nextState.setIn(['rows', rowIndex, 'selected'], cellSpec);

      return this.setStateAndTrigger(nextState);
    }

    //Only support multi-selection of data and time cells
    if (
      keyBinder.shift &&
      !_isMultiSelectable(cellSpec.get('type')) &&
      !disableBulkAction
    )
      return;

    if (keyBinder.shift && !disableBulkAction) {
      //Deselect all special cells when in multi-cell select mode
      nextState = this.deselectSpecialCells(nextState);
      nextState = this.selectCellRange(rowIndex, cellSpec, nextState);
    }

    if ((keyBinder.shift && disableBulkAction) || !keyBinder.shift) {
      /**
       * Use clearRowFieldState (see CuesheetCoreMixin).
       * Then apply 'selected' value to the row that user has selected
       *
       * Fix for Issue#2078 (mobile shotgun issue)
       */
      nextState = this.clearRowFieldState('selected');

      /**
       * This is the legacy way of accounting for cue mobile shotgun
       * view. This will be RETIRED soon so no refactor will be done
       * as this will be removed in the new future.
       */
      nextState = nextState.setIn(['rows', rowIndex, 'selected'], cellSpec);

      nextState = this.deselectAllCells(nextState);
      nextState = this.selectCell(rowIndex, cellSpec, nextState);
    }

    nextState = nextState
      .set('lastAction', 'setCursor')
      .set('lastActionTimestamp', new Date().valueOf())
      .set('scrollRowIndex', -1)
      .set('scrollColumnIndex', -1)
      .delete('scrollRowWithOffsetIndex')
      .set('keyScope', KeyScopes.Default);

    /**
     * Scroll table
     */
    nextState = nextState.withMutations((mutableState) => {
      if (!skipScroll) {
        //Update the scroll index (if necessary)

        const displayType = this.getInState('show').get('displayType');

        if (
          displayType &&
          (displayType === DisplayTypes.show ||
            displayType === DisplayTypes.globalElementContainer)
        ) {
          mutableState
            .set('scrollRowIndex', rowIndex)
            .set('scrollColumnIndex', this._getScrollColumnIndex(cellSpec));
        } else {
          mutableState.set('scrollToSelectedCell', true);
        }
      }

      return mutableState;
    });

    //Used for feature view to determine what the active row index is.
    nextState = nextState.set('featuredCellRowIndex', rowIndex);

    this.setStateAndTrigger(nextState);
  },

  onDeleteScrollToSelectedCell() {
    this.setStateAndTrigger(this.getState().delete('scrollToSelectedCell'));
  },

  /**
   * Deselect all cells that are on a specific rowId
   *
   * @param rowIndex
   * @param state {Optional}
   */
  deselectAllCellsWithRowIndex(rowIndex, state) {
    let nextState = state || this.getState();

    nextState = nextState.update('selectedCells', (selectedCells) => {
      return selectedCells.filter((cell) => {
        return cell.rowId !== this.findRowId(rowIndex, nextState);
      });
    });

    nextState = this.touchRow(rowIndex, nextState);

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Delete all cell selections that are in one of the rowIds
   *
   * @param rowIds
   * @param state {Optional}
   */
  removeCellSelectionsByRowIds(rowIds, state) {
    let nextState = state || this.getState();

    if (
      !nextState.get('selectedCells') ||
      nextState.get('selectedCells').size < 1
    )
      return nextState;

    if (!Array.isArray(rowIds)) rowIds = [rowIds];

    nextState = nextState.update('selectedCells', (selectedCells) => {
      selectedCells.forEach((cell, key) => {
        if (rowIds.indexOf(cell.rowId) !== -1) {
          selectedCells = selectedCells.delete(key);
        }
      });

      return selectedCells;
    });

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Delete all cell selections that are in one of the rowIds
   *
   * @param rowIds
   * @param state {Optional}
   */
  removeCellSelectionsByColumnIds(columnIds, state) {
    let nextState = state || this.getState();

    if (
      !nextState.get('selectedCells') ||
      nextState.get('selectedCells').size < 1
    )
      return nextState;

    if (!Array.isArray(columnIds)) columnIds = [columnIds];

    nextState = nextState.update('selectedCells', (selectedCells) => {
      selectedCells.forEach((cell, key) => {
        if (columnIds.indexOf(cell.cellSpec.get('value')) !== -1) {
          selectedCells = selectedCells.delete(key);
        }
      });

      return selectedCells;
    });

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Remove all selected cell state
   */
  deselectAllCells(state) {
    let nextState = state || this.getState();

    //All rows should be touched before selectedCells are wiped out
    nextState = this.touchAllSelectedCellRows(nextState);

    nextState = nextState.set('selectedCells', OrderedMap());

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Remove selection state on any special cell (item number, item title, note, etc.)
   *
   * Why would we do this?
   * We do this because in multi-cell select mode we only are allowed to select
   * data cells. This will remove any special cell selections and leave the data
   * cells selections.
   */
  deselectSpecialCells(state) {
    const itemSetId = state.get('rowMap').keySeq().first();

    state.get('selectedCells').forEach((cell, key) => {
      if (!_isMultiSelectable(cell.cellSpec.get('type'))) {
        state = this.touchRow(findRowPosition(itemSetId, cell.rowId, state));
        state = state.deleteIn(['selectedCells', key]);
      }
    });

    return state;
  },

  /**
   * Selects first cell in a row by the rowIndex
   *
   * Why would you want to do this?
   * If you have multiple cells selected and add a global highlight, lock, or
   * any other row specific action we want to select the cell and make that row
   * active.
   *
   * @param rowIndex
   * @param state {Optional}
   */
  selectFirstCellInRowWithRowIndex(rowIndex, state) {
    let nextState = state || this.getState();
    const columnId = nextState.get('columnOrder').toJS().shift(),
      cellSpec = Map({ type: EditableCellTypes.Data, value: columnId });

    nextState = this.deselectAllCells(nextState);
    nextState = this.selectCell(rowIndex, cellSpec, nextState);

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  onSelectLastCellSelected() {
    // eslint-disable-next-line no-unused-vars
    const next = this.selectLastCellSelected();

    // TODO complete this...
  },

  selectLastCellSelected(state) {
    return this.deselectAllCellsExceptLast(state);
  },

  /**
   * Deselect all cells except the last cell selection.
   *
   * @param state {Optional}
   */
  deselectAllCellsExceptLast(state) {
    let nextState = state || this.getState();

    const lastSelectedCell = this.getLastSelectedCell(nextState);

    if (!lastSelectedCell) return nextState;

    const { rowIndex, cellSpec } = lastSelectedCell;

    nextState = nextState.set('featuredCellRowIndex', rowIndex);

    if (nextState.get('selectedCells').size < 2) return nextState;

    nextState = this.deselectAllCells(nextState);

    nextState = this.selectCell(rowIndex, cellSpec, nextState);

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Deselect all cells except the first cell selection.
   *
   * @param state {Optional}
   */
  deselectAllCellsExceptFirst(state) {
    let nextState = state || this.getState();

    if (nextState.get('selectedCells').size < 2) return nextState;

    const { rowIndex, cellSpec } = this.getFirstSelectedCell(nextState);
    nextState = this.deselectAllCells(nextState);

    nextState = this.selectCell(rowIndex, cellSpec, nextState);

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Select Cell
   *
   * @param rowIndex
   * @param cellSpec
   */
  selectCell(rowIndex, cellSpec, state) {
    let nextState = state || this.getState();
    const rowId = this.findRowId(rowIndex, nextState),
      selectedCellPath = [
        'selectedCells',
        this.getSelectedCellKey(rowId, cellSpec),
      ];
    /*
     * Touch row so that selection state gets rendered
     */
    nextState = this.touchRow(rowIndex, nextState);

    /**
     * If cell already exists in selectedCells delete it. This will cause the
     * cell to be reset into selected cells making it the last selected cell in
     * the list.
     */
    if (nextState.getIn(selectedCellPath))
      nextState = nextState.deleteIn(selectedCellPath);

    nextState = nextState.setIn(selectedCellPath, { rowId, cellSpec });

    /**
     * If state is specified then return updated state
     */
    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Select cells in range
   *
   * If rowIndex & cellSpec are not passed in then it will not select any new
   * cell, it will only reselect the cell range.
   *
   * Why would we do this?
   * If someone adds a new row and it appears realtime between
   * any rows with selected cells we want that new row to also have a selection
   * state. This only happens by recalculating the selection state.
   *
   * @param rowIndex {Optional}
   * @param cellSpec {Optional}
   * @param state {Required}
   */
  selectCellRange(rowIndex, cellSpec, state) {
    let nextState = state;

    const setCellSelection = typeof rowIndex === 'number' && cellSpec;

    /**
     * We do this because _selectCellRange needs the latest selection in state in order to select the range
     */
    if (setCellSelection)
      nextState = this.selectCell(rowIndex, cellSpec, nextState);

    /**
     * Figure out range of rows that should have cells selected
     */
    const beginRowIndex = this.getFirstSelectedCellRowIndex(nextState),
      endRowIndex = this.getLastSelectedCellRowIndex(nextState);

    //If no cells are selected, return the original state
    if (beginRowIndex === -1 && endRowIndex === -1) return state;

    const firstSelectedCellType =
      this.getFirstSelectedCell(nextState).cellSpec.get('type');

    if (
      firstSelectedCellType === EditableCellTypes.ItemDuration ||
      firstSelectedCellType === EditableCellTypes.ItemPresetDuration ||
      firstSelectedCellType === EditableCellTypes.ItemStartTime
    ) {
      nextState = this._selectTimingCellRange(
        beginRowIndex,
        endRowIndex,
        nextState,
      );
    } else {
      nextState = this._selectCellRange(beginRowIndex, endRowIndex, nextState);
    }

    return nextState;
  },

  /**
   * Select cells within specified range
   */
  _selectCellRange(beginRowIndex, endRowIndex, state) {
    let nextState = state || this.getState();

    /**
     * Get index position of selected columnIds in columnOrder state property
     */

    const firstSelectedCellSpec = this.getFirstSelectedCell(nextState).cellSpec,
      lastSelectedCellSpec = this.getLastSelectedCell(nextState).cellSpec,
      columnOrder = nextState.get('columnOrder');

    //Default to the first and last
    let firstSelectedColumnId, lastSelectedColumnId;

    let firstSelectedTitle = false,
      firstSelectedDesc = false,
      firstSelectedNote = false,
      lastSelectedTitle = false,
      lastSelectedDesc = false,
      lastSelectedStartTime = false,
      lastSelectedPresetDuration = false,
      lastSelectedDuration = false,
      lastSelectedNote = false;

    if (firstSelectedCellSpec.get('value')) {
      firstSelectedColumnId = firstSelectedCellSpec.get('value');
    } else if (
      firstSelectedCellSpec.get('type') === EditableCellTypes.ItemTitle
    ) {
      firstSelectedTitle = true;
    } else if (
      firstSelectedCellSpec.get('type') === EditableCellTypes.ItemDescription
    ) {
      firstSelectedDesc = true;
    } else if (
      firstSelectedCellSpec.get('type') === EditableCellTypes.ItemNote
    ) {
      firstSelectedNote = true;
    }

    if (lastSelectedCellSpec.get('value')) {
      lastSelectedColumnId = lastSelectedCellSpec.get('value');
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemTitle
    ) {
      lastSelectedTitle = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemDescription
    ) {
      lastSelectedDesc = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemStartTime
    ) {
      lastSelectedStartTime = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemPresetDuration
    ) {
      lastSelectedPresetDuration = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemDuration
    ) {
      lastSelectedDuration = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemNote
    ) {
      lastSelectedNote = true;
    }

    const firstSelectedColumnOrderIndex = firstSelectedColumnId
      ? columnOrder.indexOf(firstSelectedColumnId)
      : 0;
    const lastSelectedColumnOrderIndex = lastSelectedColumnId
      ? columnOrder.indexOf(lastSelectedColumnId)
      : columnOrder.last();

    let beginColumnIndex = 0,
      endColumnIndex = 0;

    if (firstSelectedColumnOrderIndex <= lastSelectedColumnOrderIndex) {
      beginColumnIndex = firstSelectedColumnOrderIndex;
      endColumnIndex = lastSelectedColumnOrderIndex;
    } else {
      beginColumnIndex = lastSelectedColumnOrderIndex;
      endColumnIndex = firstSelectedColumnOrderIndex;
    }

    /**
     *
     * This slices the chunk of column ids that will be in selected cell range.
     */
    let columnIds = columnOrder.slice(beginColumnIndex, endColumnIndex + 1);

    /**
     * Correctly order columns. This is important for column selections.
     */
    columnIds = columnIds.sort((a, b) => {
      const aIndex = columnOrder.indexOf(a);
      const bIndex = columnOrder.indexOf(b);

      if (firstSelectedColumnOrderIndex < lastSelectedColumnOrderIndex) {
        if (aIndex < bIndex) return -1;
        if (aIndex > bIndex) return 1;
      } else {
        if (aIndex > bIndex) return -1;
        if (aIndex < bIndex) return 1;
      }

      return 0;
    });

    /**
     * We have to deselect the old cells before selecting the new ones
     */
    nextState = this.deselectAllCells(nextState);

    const _selectTitle = (i) =>
      this.selectCell(i, Map({ type: EditableCellTypes.ItemTitle }), nextState);

    const _selectDesc = (i) => {
      //If item descriptions hidden, don't add to selection
      if (!nextState.getIn(['show', 'displayItemDescriptions']) || isCompact())
        return nextState;
      return this.selectCell(
        i,
        Map({ type: EditableCellTypes.ItemDescription }),
        nextState,
      );
    };

    const _selectNote = (i) => {
      if (nextState.getIn(['userSettings', 'noteColumn', 'hidden']))
        return nextState;
      return this.selectCell(
        i,
        Map({ type: EditableCellTypes.ItemNote }),
        nextState,
      );
    };

    const _selectCells = (i) => {
      /**
       * NOTE: this convoluted logic will be shortlived, assuming we rework how column data is stored
       */

      /**
       * If the first cell selected is a title, select the desc and note cells if the last selected cell is one of these types of cells
       * or a data cell
       */

      if (firstSelectedTitle) {
        nextState = _selectTitle(i);
        if (
          lastSelectedDesc ||
          lastSelectedStartTime ||
          lastSelectedPresetDuration ||
          lastSelectedDuration ||
          lastSelectedNote ||
          lastSelectedColumnId
        )
          nextState = _selectDesc(i);
        if (lastSelectedNote || lastSelectedColumnId)
          nextState = _selectNote(i);

        /**
         * If the first cell selected is a desc, select the note cell is a note or data cell
         * or a data cell
         */
      } else if (firstSelectedDesc) {
        nextState = _selectDesc(i);
        if (lastSelectedNote || lastSelectedColumnId)
          nextState = _selectNote(i);
        /**
         * If the first cell selected is a note, select the note cell
         */
      } else if (firstSelectedNote) {
        nextState = _selectNote(i);
      }

      /**
       * Only select data columns if either the first or last selected is a data cell
       */
      if (firstSelectedColumnId || lastSelectedColumnId) {
        columnIds.forEach((id) => {
          nextState = this.selectCell(
            i,
            Map({ type: EditableCellTypes.Data, value: id }),
            nextState,
          );
        });
      }

      /**
       * If the last selected cell is a title, and the first selected cell is a desc, note, or data type
       */
      if (lastSelectedTitle) {
        nextState = _selectTitle(i);
        if (firstSelectedDesc || firstSelectedNote || firstSelectedColumnId)
          nextState = _selectDesc(i);
        if (firstSelectedNote || firstSelectedColumnId)
          nextState = _selectNote(i);
        /**
         * If the last selected cell is a desc, select the desc, and then if the first selected cell is a data cell or a note, select the note cell
         */
      } else if (lastSelectedDesc) {
        nextState = _selectDesc(i);
        if (firstSelectedNote || firstSelectedColumnId)
          nextState = _selectNote(i);
        /**
         * If the last selected cell is note, select the note cell
         */
      } else if (lastSelectedStartTime) {
        if (firstSelectedNote || firstSelectedColumnId)
          nextState = _selectNote(i);
      } else if (lastSelectedPresetDuration) {
        if (firstSelectedNote || firstSelectedColumnId)
          nextState = _selectNote(i);
      } else if (lastSelectedDuration) {
        if (firstSelectedNote || firstSelectedColumnId)
          nextState = _selectNote(i);
      } else if (lastSelectedNote) {
        nextState = _selectNote(i);
      }
    };

    /**
     * Set selection state on cells
     *
     * Step 1: Loop throw all rows in selection range
     *  Step 1.1: Add a selection state to each cell in the row within the
     *  selection range.
     *
     * Why do we have to change the direction of the for loop?
     * This is important because of the way that the last cell selection state
     * works.
     */
    if (beginRowIndex < endRowIndex) {
      for (let i = beginRowIndex; i <= endRowIndex; i++) {
        _selectCells(i);
      }
    } else {
      /**
       * beginRowIndex > endRowIndex
       * When moving cursor (cell selection) in an upward direction
       */
      for (let i = beginRowIndex; i >= endRowIndex; i--) {
        _selectCells(i);
      }
    }

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Select cells within specified range
   */
  _selectTimingCellRange(beginRowIndex, endRowIndex, state) {
    let nextState = state || this.getState();

    /**
     * Get index position of selected columnIds in columnOrder state property
     */

    const firstSelectedCellSpec = this.getFirstSelectedCell(nextState).cellSpec,
      lastSelectedCellSpec = this.getLastSelectedCell(nextState).cellSpec;

    //Default to the first and last
    let lastSelectedColumnId;

    let firstSelectedStartTime = false,
      firstSelectedPresetDuration = false,
      firstSelectedDuration = false,
      lastSelectedTitle = false,
      lastSelectedDesc = false,
      lastSelectedStartTime = false,
      lastSelectedPresetDuration = false,
      lastSelectedDuration = false,
      lastSelectedNote = false;

    if (firstSelectedCellSpec.get('type') === EditableCellTypes.ItemStartTime) {
      firstSelectedStartTime = true;
    } else if (
      firstSelectedCellSpec.get('type') === EditableCellTypes.ItemPresetDuration
    ) {
      firstSelectedPresetDuration = true;
    } else if (
      firstSelectedCellSpec.get('type') === EditableCellTypes.ItemDuration
    ) {
      firstSelectedDuration = true;
    }

    if (lastSelectedCellSpec.get('value')) {
      lastSelectedColumnId = lastSelectedCellSpec.get('value');
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemTitle
    ) {
      lastSelectedTitle = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemDescription
    ) {
      lastSelectedDesc = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemStartTime
    ) {
      lastSelectedStartTime = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemPresetDuration
    ) {
      lastSelectedPresetDuration = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemDuration
    ) {
      lastSelectedDuration = true;
    } else if (
      lastSelectedCellSpec.get('type') === EditableCellTypes.ItemNote
    ) {
      lastSelectedNote = true;
    }

    /**
     * We have to deselect the old cells before selecting the new ones
     */
    nextState = this.deselectAllCells(nextState);

    const _selectStartTime = (i) => {
      if (!nextState.getIn(['show', 'displayStartTime'])) return nextState;
      return this.selectCell(
        i,
        Map({ type: EditableCellTypes.ItemStartTime }),
        nextState,
      );
    };

    const _selectPreset = (i) => {
      if (!nextState.getIn(['show', 'displayPresetDuration'])) return nextState;
      return this.selectCell(
        i,
        Map({ type: EditableCellTypes.ItemPresetDuration }),
        nextState,
      );
    };

    const _selectDuration = (i) => {
      if (!nextState.getIn(['show', 'displayDuration'])) return nextState;
      return this.selectCell(
        i,
        Map({ type: EditableCellTypes.ItemDuration }),
        nextState,
      );
    };

    const _selectCells = (i) => {
      /**
       * NOTE: this convoluted logic will be shortlived, assuming we rework how column data is stored
       */

      if (firstSelectedStartTime) {
        nextState = _selectStartTime(i);
        if (isGrid()) {
          if (
            lastSelectedPresetDuration ||
            lastSelectedDuration ||
            lastSelectedTitle ||
            lastSelectedDesc ||
            lastSelectedColumnId
          ) {
            nextState = _selectPreset(i);
          }
          if (
            lastSelectedDuration ||
            lastSelectedTitle ||
            lastSelectedDesc ||
            lastSelectedColumnId
          ) {
            nextState = _selectDuration(i);
          }
        } else {
          if (
            lastSelectedPresetDuration ||
            lastSelectedDuration ||
            lastSelectedNote ||
            lastSelectedColumnId
          ) {
            nextState = _selectPreset(i);
          }
          if (
            lastSelectedDuration ||
            lastSelectedNote ||
            lastSelectedColumnId
          ) {
            nextState = _selectDuration(i);
          }
        }
      } else if (firstSelectedPresetDuration) {
        nextState = _selectPreset(i);
        if (isGrid()) {
          if (
            lastSelectedDuration ||
            lastSelectedTitle ||
            lastSelectedDesc ||
            lastSelectedColumnId
          ) {
            nextState = _selectDuration(i);
          }
          if (lastSelectedStartTime || lastSelectedNote) {
            nextState = _selectStartTime(i);
          }
        } else {
          if (
            lastSelectedDuration ||
            lastSelectedNote ||
            lastSelectedColumnId
          ) {
            nextState = _selectDuration(i);
          }
          if (lastSelectedStartTime) {
            nextState = _selectStartTime(i);
          }
        }
      } else if (firstSelectedDuration) {
        nextState = _selectDuration(i);
        if (isGrid()) {
          if (
            lastSelectedPresetDuration ||
            lastSelectedStartTime ||
            lastSelectedNote
          ) {
            nextState = _selectPreset(i);
          }
          if (lastSelectedStartTime || lastSelectedNote) {
            nextState = _selectStartTime(i);
          }
        } else {
          if (
            lastSelectedPresetDuration ||
            lastSelectedStartTime ||
            lastSelectedTitle ||
            lastSelectedDesc
          ) {
            nextState = _selectPreset(i);
          }
          if (lastSelectedStartTime || lastSelectedTitle || lastSelectedDesc) {
            nextState = _selectStartTime(i);
          }
        }
      }
    };

    /**
     * Set selection state on cells
     *
     * Step 1: Loop throw all rows in selection range
     *  Step 1.1: Add a selection state to each cell in the row within the
     *  selection range.
     *
     * Why do we have to change the direction of the for loop?
     * This is important because of the way that the last cell selection state
     * works.
     */
    if (beginRowIndex < endRowIndex) {
      for (let i = beginRowIndex; i <= endRowIndex; i++) {
        _selectCells(i);
      }
    } else {
      /**
       * beginRowIndex > endRowIndex
       * When moving cursor (cell selection) in an upward direction
       */
      for (let i = beginRowIndex; i >= endRowIndex; i--) {
        _selectCells(i);
      }
    }

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Recalculate Cell Selection Range
   *
   * Useful for things like new rows or columns being added in realtime.
   *
   * @param state {Optional}
   */
  recalculateCellSelectionRange(state) {
    let nextState = state || this.getState();

    if (
      !nextState.get('selectedCells') ||
      nextState.get('selectedCells').size < 1
    )
      return nextState;

    nextState = this.selectCellRange(null, null, nextState);

    if (state) return nextState;

    this.setStateAndTrigger(nextState);
  },

  /**
   * Get all unique column ids from selected cells
   */
  _getAllUniqueColumnIdsFromCellSelections(state) {
    const nextState = state || this.getState();

    /**
     * Get all unique columns
     */
    const columnIds = [];

    nextState.get('selectedCells').forEach((cell) => {
      const columnId = cell.getIn(['cellSpec', 'value']);
      if (columnIds.indexOf(columnId) !== -1) return;
      columnIds.push(columnId);
    });

    return columnIds;
  },

  /**
   * Get selected cell key.
   *
   * Data Cell Key: rowId_columnId
   * Special Cell Key (Note, Item, etc.) rowId_type
   *
   * @param rowId
   * @param cellSpec
   */
  getSelectedCellKey(rowId, cellSpec) {
    const cellIdentifier =
      cellSpec.get('value') && cellSpec.get('type') === EditableCellTypes.Data
        ? cellSpec.get('value')
        : cellSpec.get('type');

    return `${rowId}_${cellIdentifier}`;
  },

  /**
   * Do the selected cells form a rectangle?
   *
   * Why do we need this?
   * Only rectangular selection groups are allowed to be copied and pasted.
   * This validates that restriction.
   */
  selectedCellsFormValidRectangle() {
    const itemSetId = this.findFirstItemSetId();

    /**
     * 1. Format all rows into an array.
     * 2. Sort rows indexes
     * 3. Determine if the rows are incrementally correct.
     * 4. Check to see if the row cell selections are equal to one another.
     */
    const rows = {};

    const state = this.getState();

    this.getInState('selectedCells').forEach((cell, key) => {
      const keySplit = key.split('_'),
        rowId = keySplit[0],
        columnId = keySplit[1],
        rowIndex = findRowPosition(itemSetId, rowId, state);

      if (!rows[rowIndex]) rows[rowIndex] = [];
      rows[rowIndex].push(columnId);
    });

    let prevRowSelection = null;
    const invalid = Object.keys(rows).findIndex((rowIndex) => {
      const currentRowSelection = rows[rowIndex].length;

      if (prevRowSelection && prevRowSelection !== currentRowSelection)
        return true;

      prevRowSelection = currentRowSelection;

      return false;
    });

    /**
     * If row with invalid number of selections is found then return false
     * otherwise return true.
     */
    return invalid !== -1 ? false : true;
  },

  _getScrollColumnIndex(cellSpec) {
    const maxColumnIndex = this.getInState('selectableCells').size;

    let scrollColumnIndex = this._getCellIndex(cellSpec);

    //if the selected cell is 0-3, set it to 0 (so that scrolling happens before you get to the left edge)
    if (scrollColumnIndex <= 3) {
      scrollColumnIndex = 0;
    } else {
      //else plus three (so that scrolling happens before you get to the right edge)
      scrollColumnIndex += 3;
    }

    //If the scroll index is greater than the max, set it to the max
    if (scrollColumnIndex >= maxColumnIndex) scrollColumnIndex = maxColumnIndex;

    return scrollColumnIndex;
  },

  /**
   * Return new state that has no cursor selection
   *
   */
  getClearedCursorState() {
    let state = this.getState();

    state = this.touchAllSelectedCellRows(state);

    state = this.clearColumnFieldState('selected', state);

    state = this.clearRowFieldState('editing', state);
    state = this.deselectAllCells(state);

    state = state
      .set('scrollRowIndex', -1)
      .set('scrollColumnIndex', -1)
      .set('keyScope', KeyScopes.Default);

    return state;
  },

  /**
   * Return new state that has no cursor selection
   *
   */
  getCursorState() {
    let state = this.getState();

    state = this.clearColumnFieldState('selected', state);
    state = this.clearRowFieldState('editing', state);

    state = state
      .set('scrollRowIndex', -1)
      .set('scrollColumnIndex', -1)
      .set('keyScope', KeyScopes.Default);

    return state;
  },

  /**
   * Get row path to selected item
   */
  _getSelectedItemRowPath() {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { rowIndex } = selectedCell,
      rowSpec = this.getInState(['rows', rowIndex]),
      rowPath = rowSpec.get('path');

    return rowPath;
  },

  /**
   * Get data cell to the right
   */
  _selectableCellsWithoutTimes() {
    const selectableCells = this.getInState(['selectableCells']);

    const selectableCellsWithoutTimes = selectableCells.filter((cell) => {
      return (
        cell.get('type') !== EditableCellTypes.ItemStartTime &&
        cell.get('type') !== EditableCellTypes.ItemPresetDuration &&
        cell.get('type') !== EditableCellTypes.ItemDuration
      );
    });

    return selectableCellsWithoutTimes;
  },

  /**
   * Get raw text for currently selected data cell
   */
  getSelectedDataCellText() {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { cellSpec } = selectedCell;
    const rowPath = this._getSelectedItemRowPath();

    if (cellSpec.get('type') !== EditableCellTypes.Data) return;

    const cellPath = ['rowMap'].concat(rowPath, 'cells', cellSpec.get('value')),
      cellData = this.getInState(cellPath);

    return cellData ? cellData.get('text') : '';
  },

  /**
   * Get rteState for currently selected data cell
   */
  getSelectedDataCellState() {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const { cellSpec } = selectedCell;
    const rowPath = this._getSelectedItemRowPath();

    if (cellSpec.get('type') !== EditableCellTypes.Data) return;

    const cellPath = ['rowMap'].concat(rowPath, 'cells', cellSpec.get('value')),
      cellData = this.getInState(cellPath);

    return cellData && cellData.get('rteState')
      ? cellData.get('rteState')
      : getDefaultRTEState();
  },

  /**
   * Get text for currently selected item description
   */
  getSelectedItemFieldText(field) {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const rowPath = this._getSelectedItemRowPath();

    const itemPath = ['rowMap'].concat(rowPath),
      item = this.getInState(itemPath);

    return item.get(field) ? item.get(field) : '';
  },

  /**
   * Get rteState for currently selected item field
   */
  getSelectedItemFieldState(field) {
    const selectedCell = this.getSelectedCell();

    if (!selectedCell) return;

    const rowPath = this._getSelectedItemRowPath();

    const itemPath = ['rowMap'].concat(rowPath),
      item = this.getInState(itemPath);

    return item.get(field) ? item.get(field) : getDefaultRTEState();
  },

  /**
   * Get text for currently selected item note
   */
  getSelectedItemNoteText() {
    const state = this.getState();

    const selectedCell = this.getSelectedCell();
    if (!selectedCell) return;

    const { cellSpec } = selectedCell;
    const rowPath = this._getSelectedItemRowPath();

    if (cellSpec.get('type') !== EditableCellTypes.ItemNote) return;

    const cellPath = ['rowMap'].concat(
        rowPath,
        'userSettings',
        state.get('userId'),
      ),
      userSettingsObj = this.getInState(cellPath),
      //should be rteState not text?
      text =
        userSettingsObj && userSettingsObj.get('note')
          ? userSettingsObj.getIn(['note', 'text'])
          : '';

    return text;
  },

  /**
   * Get rteState for currently selected item note
   */
  getSelectedItemNoteState() {
    const state = this.getState();

    const selectedCell = this.getSelectedCell();
    if (!selectedCell) return;

    const { cellSpec } = selectedCell;
    const rowPath = this._getSelectedItemRowPath();

    if (cellSpec.get('type') !== EditableCellTypes.ItemNote) return;

    const cellPath = ['rowMap'].concat(
        rowPath,
        'userSettings',
        state.get('userId'),
      ),
      userSettingsObj = this.getInState(cellPath),
      rteState =
        userSettingsObj && userSettingsObj.get('note')
          ? userSettingsObj.getIn(['note', 'rteState'])
          : getDefaultRTEState();

    return rteState;
  },

  /**
   * Check for any cells selected in a locked row
   *
   * @returns Boolean
   */
  lockedRowCellSelections(state) {
    const nextState = state || this.getState(),
      itemSetId = nextState.get('rowMap').keySeq().first();

    const lockedRow = nextState.get('selectedCells').find((cell) => {
      return this.getItemRowLockStatus(
        findRowPosition(itemSetId, cell.rowId, state),
      );
    });

    return lockedRow ? true : false;
  },

  /**
   * Check if any cells selections are special cells: Item Titles, Item
   * Descriptions, or Private Notes
   *
   * returns Boolean
   */
  specialCellSelections(state) {
    const nextState = state || this.getState();

    const specialCell = nextState
      .get('selectedCells')
      .find((cell) => !_isMultiSelectable(cell.cellSpec.get('type')));

    return specialCell ? true : false;
  },

  isMultiCellSelectable: _isMultiSelectable,
};
